// @flow
import React from 'react';
import type { CompilerInjectedProps } from './validation/runtime-checks/index.js';
import { usePropsValidation } from './validation/runtime-checks/index.js';
import { types as t } from './validation/runtime-checks/index.js';
import Figure from './Figure.js';
import { figureWidths } from './designSystem.js';

const UglyLegacyImage = (props: {|
  /** A globally unique id to help coordinate the creation of a new, non-sucky version of this image */
  id: string,
  /** Should typically be something like: "/media/upload/2020/Geometry/tri/10right20.svg" */
  url: string,
  /** The description that screenreaders will use to describe the image to a user */
  accessibilityDescription?: string, // TODO can make mandatory later
  // Dimensions are mandatory so that the layout can be computed
  // by the browser before any image data has been received.
  width: number,
  height: number,
  ...CompilerInjectedProps,
|}) => {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    UglyLegacyImage,
  );
  const { _performValidation, url, accessibilityDescription, width, height } =
    props;

  const imageRef = React.createRef();

  function validateAspectRatio() {
    if (!_performValidation) return;
    if (imageRef.current !== null && imageRef.current.complete) {
      assertNonDistortedAspectRatio(imageRef.current, addErrors, locations);
    }
  }

  // When we are re-previewing a document, the image is typically already loaded.
  // Thus we need to run this check on every render, and we only use the onLoad
  // callback of the img element on the initial render of a given img src.
  React.useEffect(validateAspectRatio);

  if (failedValidation) return null;
  return (
    <Figure>
      <img
        ref={imageRef}
        src={url}
        alt={accessibilityDescription}
        width={width}
        height={height}
        onLoad={validateAspectRatio}
      />
    </Figure>
  );
};
UglyLegacyImage.spec = {
  id: t.uuid,
  url: urlChecker,
  accessibilityDescription: t.optional(t.string),
  width: figureWidthChecker,
  height: t.number,
};

function figureWidthChecker(props, propName) {
  const value = props[propName];

  const failure = t.number(props, propName);
  if (failure !== null) return failure;

  if (figureWidths.includes(value) === false) {
    return {
      typeDescription: figureWidthChecker.description,
      reason: `must be one of the following values: ${figureWidths.join(', ')}`,
    };
  }

  return null;
}
figureWidthChecker.description = 'figureWidth';

function urlChecker(props, propName) {
  const value = props[propName];
  try {
    if (typeof value !== 'string') throw Error('must be a string');
    // This will throw if a malformed URL is passed (eg. only providing a path, not a fully-qualified URL)
    new URL(value);
  } catch (error) {
    return {
      typeDescription: urlChecker.description,
      reason: 'must be a valid URL. Eg. http://blah.com/image.png',
    };
  }

  return null;
}
urlChecker.description = 'URL';

// Dupe of what is in Image.js, but we hope to delete this component at some point
// so decreasing coupling is fine.
function assertNonDistortedAspectRatio(img, addErrors, locations) {
  if (img === null) return;
  const { naturalWidth, naturalHeight } = img;
  // NOTE: do NOT use img.width/height as this reports an integer value, not the attribute value.
  const width = Number(img.getAttribute('width') ?? 0);
  const height = Number(img.getAttribute('height') ?? 0);
  const naturalAspectRatio = naturalWidth / naturalHeight;
  const actualAspectRatio = width / height;
  const tolerance = 0.00001;
  if (Math.abs(naturalAspectRatio - actualAspectRatio) > tolerance) {
    console.error('assertNonDistortedAspectRatio failed', {
      width,
      height,
      naturalWidth,
      naturalHeight,
      naturalAspectRatio,
      actualAspectRatio,
      tolerance,
      abs: Math.abs(naturalAspectRatio - actualAspectRatio),
    });
    // TODO: *if* the Chrome bug only ever affects the width/height then we could use the JSX declared
    // dimensions rather than using the erroneous DOM API to avoid the issue.
    const zoom = getBrowserZoom();
    const suggestedDimensions = figureWidths.map(
      (w) => `- ${w}×${w / naturalAspectRatio}`,
    );
    addErrors([
      {
        message: [
          `The width + height you have chosen distorts the aspect ratio of the image.`,
          zoom !== '100%' &&
            ` 🚨 Your zoom is set to ${zoom}. There is a Chrome bug that misreports the image size when zoom is not 100%. If you think you have set the dimensions correctly, try fixing your zoom to resolve the issue.`,
          `Use one of the following dimensions for your image:`,
          ...suggestedDimensions,
        ]
          .filter(Boolean)
          .join('\n'),
        ...locations.UglyLegacyImage,
      },
    ]);
  }
}

function getBrowserZoom() {
  return Math.round((window.outerWidth / window.innerWidth) * 100) + '%';
}

export default UglyLegacyImage;
